import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

export interface NoteDto {
  id: number;
  body: string;
  created_by: number;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete: boolean;
}

interface ListNotesResponse {
  notes: NoteDto[];
}

interface NoteResponse {
  note: NoteDto;
}

function notesKey(entityType: string, encId: string) {
  return ['notes', entityType, encId] as const;
}

export function useEntityNotes(entityType: 'receipt' | 'wb_ticket', encId: string | undefined) {
  return useQuery<ListNotesResponse>({
    queryKey: notesKey(entityType, encId ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<ListNotesResponse>(
        `/notes/${entityType}/${encId ?? ''}`,
      );
      return res.data;
    },
    enabled: !!encId,
  });
}

export function useCreateNote(entityType: 'receipt' | 'wb_ticket', encId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      apiClient.post<NoteResponse>(`/notes/${entityType}/${encId}`, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notesKey(entityType, encId) });
    },
  });
}

export function useUpdateNote(entityType: 'receipt' | 'wb_ticket', encId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, body }: { noteId: number; body: string }) =>
      apiClient.patch<NoteResponse>(`/notes/${entityType}/${encId}/${noteId}`, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notesKey(entityType, encId) });
    },
  });
}

export function useDeleteNote(entityType: 'receipt' | 'wb_ticket', encId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: number) =>
      apiClient.delete(`/notes/${entityType}/${encId}/${noteId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notesKey(entityType, encId) });
    },
  });
}
